import {
  lijuItemsPath,
  lijuPath,
  savedLijuField,
  savedLijuIntentField,
  savedLijuIntents,
} from "../user/save-liju-routes.js";
import { drawSavedLiju } from "./save-liju-draw.js";
import type { StandingPanel } from "./study-standing.js";
import { standingPanelFor } from "./study-standing-badge.js";

/**
 * What one saved-sentence control does when it is pressed.
 *
 * Apart from `save-liju.ts`, which finds the controls and asks which sentences
 * a reader already holds, in the shape `save-word-press.ts` takes for words.
 */

/** Whether the page says there is a reader to ask the route about. */
export function hasSession(document: Document): boolean {
  return document.documentElement.dataset["cbSession"] === "in";
}

/**
 * Save or take out one sentence, and say what happened to it.
 *
 * The press asks for what the button offers. A page drawn before the sentence
 * was saved in another tab still offers to save it (CB-44).
 *
 * `full` is a free reader at the cap, whom the page sends to their sentences,
 * where the cap is explained. `null` is a reader with no session.
 */
async function press(
  fetched: typeof fetch,
  button: HTMLButtonElement,
): Promise<readonly string[] | null | "full"> {
  const intent = button.hasAttribute("data-cb-saved")
    ? savedLijuIntents.remove
    : savedLijuIntents.save;
  const body = new URLSearchParams({
    [savedLijuField]: button.value,
    [savedLijuIntentField]: intent,
  });
  const response = await fetched(lijuItemsPath, { method: "POST", body });
  const answer = (await response.json()) as {
    saved?: readonly string[] | null;
    full?: unknown;
  };

  if (answer.full === true) {
    return "full";
  }

  return answer.saved ?? null;
}

/**
 * Take the presses on one control.
 * @param properties - The control, its form and button, the page, and what
 *   asks the route.
 * @returns The panel the control opens, where it is a corner badge.
 */
export function listenForSavedLiju(properties: {
  control: HTMLElement;
  form: HTMLFormElement;
  button: HTMLButtonElement;
  document: Document;
  fetched: typeof fetch;
}): StandingPanel | undefined {
  const { control, form, button, document, fetched } = properties;

  const offer = (): void => {
    const line = control.querySelector<HTMLElement>(".cb-save-liju-offer");
    if (line !== null) {
      line.hidden = false;
    }
  };
  const panel = standingPanelFor({
    control,
    button,
    kind: "liju",
    fetched,
    remove: () => {
      turnOver();
    },
  });
  const turnOver = (): void => {
    void press(fetched, button)
      .then((saved) => {
        if (saved === "full") {
          globalThis.location.assign(lijuPath);
        } else if (saved === null) {
          offer();
        } else {
          const held = saved.includes(button.value);

          drawSavedLiju(button, held);
          // A sentence just saved opens its panel. See `study-standing.ts`.
          if (held) {
            void panel?.open().catch(() => undefined);
          }
        }
      })
      .catch(() => form.submit());
  };

  form.addEventListener("submit", (event) => {
    event.preventDefault();

    if (!hasSession(document)) {
      offer();
      return;
    }

    // A held badge opens its panel, and turns the sentence over only where
    // the handler had no panel to draw.
    if (panel !== undefined && button.hasAttribute("data-cb-saved")) {
      void panel
        .toggle()
        .then((shown) => {
          if (!shown) {
            turnOver();
          }
        })
        .catch(() => undefined);
      return;
    }

    turnOver();
  });

  return panel;
}
